import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Globe, Network as NetworkIcon } from 'lucide-react';
import { Network, Domain } from '../../types';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { useState } from 'react';

interface DomainSelectorProps {
  networks: Network[];
  selectedDomain: Domain | null;
  onDomainSelect: (domain: Domain) => void;
}

export const DomainSelector: React.FC<DomainSelectorProps> = ({
  networks,
  selectedDomain,
  onDomainSelect,
}) => {
  const { t } = useTranslation();
  const [expandedNetworks, setExpandedNetworks] = useState<Set<number>>(new Set());

  const toggleNetwork = (networkId: number) => {
    const newExpanded = new Set(expandedNetworks);
    if (newExpanded.has(networkId)) {
      newExpanded.delete(networkId);
    } else {
      newExpanded.add(networkId);
    }
    setExpandedNetworks(newExpanded);
  };

  // Calculer le nombre total de domaines
  const totalDomains = networks.reduce((total, network) => total + (network.domains?.length || 0), 0);

  if (networks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <NetworkIcon className="h-12 w-12 text-muted-foreground/30 mb-3" />
        <h3 className="text-sm font-medium text-muted-foreground mb-1">
          {t('domainServices.noNetworks')}
        </h3>
        <p className="text-xs text-muted-foreground">
          {t('domainServices.createNetworksFirst')}
        </p>
      </div>
    );
  }

  if (totalDomains === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <Globe className="h-12 w-12 text-muted-foreground/30 mb-3" />
        <h3 className="text-sm font-medium text-muted-foreground mb-1">
          {t('domainServices.noDomains')}
        </h3>
        <p className="text-xs text-muted-foreground">
          {t('domainServices.addDomainsFirst')}
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full pr-4">
      <div className="space-y-2">
        {networks.map((network) => {
          const isExpanded = expandedNetworks.has(network.id);
          const domainCount = network.domains?.length || 0;
          
          return (
            <Collapsible
              key={network.id}
              open={isExpanded}
              onOpenChange={() => toggleNetwork(network.id)}
            >
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-start p-3 h-auto hover:bg-muted/50"
                >
                  <div className="flex items-center gap-2 w-full">
                    {domainCount > 0 ? (
                      isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )
                    ) : (
                      <div className="w-4 h-4" /> // Espace pour l'alignement
                    )}
                    <NetworkIcon className="h-4 w-4 text-primary" />
                    <span className="font-medium text-sm">{network.name}</span>
                    <Badge variant="outline" className="ml-auto text-xs">
                      {domainCount}
                    </Badge>
                  </div>
                </Button>
              </CollapsibleTrigger>
              
              {domainCount > 0 && (
                <CollapsibleContent className="ml-6 mt-1">
                  <div className="space-y-1">
                    {network.domains?.map((domain) => (
                      <Button
                        key={domain.id}
                        variant={selectedDomain?.id === domain.id ? "secondary" : "ghost"}
                        className="w-full justify-start p-2 h-auto text-left"
                        onClick={() => onDomainSelect(domain)}
                      >
                        <div className="flex items-start gap-2 w-full">
                          <Globe className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs truncate">
                              {domain.name}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {domain.url}
                            </div>
                          </div>
                        </div>
                      </Button>
                    ))}
                  </div>
                </CollapsibleContent>
              )}
            </Collapsible>
          );
        })}
      </div>
    </ScrollArea>
  );
};